import type {TraceData} from '../../../utils/copilot-chat-reducer'
import type {SpanNode} from '../../../utils/tracing'

export interface TraceState {
  traceInfo?: {
    visible: boolean
    traces: Map<string, TraceData> // messageId to trace data
    loadedThreadIds: Set<string>
  }
}

export type TraceAction =
  | {
      type: 'SET_TRACE_INFO'
      traceInfo: {
        visible?: boolean
        spanNode?: SpanNode
        messageId?: string
        threadId?: string
        actualTraceId?: string
        traces?: Map<string, TraceData>
      }
    }
  | {type: 'REMOVE_TRACE'; traceID: string} // traceID is the actualTraceId, not messageId
  | {type: 'REMOVE_TRACES_FOR_THREAD'; threadID: string}

export function traceReducer(state: TraceState, action: TraceAction): TraceState {
  switch (action.type) {
    case 'SET_TRACE_INFO': {
      const {visible, spanNode, messageId, threadId, actualTraceId, traces} = action.traceInfo

      const currentTraces = new Map(state.traceInfo?.traces || [])
      const currentLoadedThreadIds = new Set(state.traceInfo?.loadedThreadIds || [])

      if (traces) {
        // Merge new traces with existing ones
        for (const [msgId, traceData] of traces) {
          currentTraces.set(msgId, traceData)
        }
      } else if (spanNode && messageId && threadId) {
        // Handle single trace update (from trace save)
        const timestamp = new Date(spanNode.startTime).toISOString()
        currentTraces.set(messageId, {
          spanNode,
          threadId,
          actualTraceId: actualTraceId || '',
          timestamp,
        })
      }

      if (threadId) {
        currentLoadedThreadIds.add(threadId)
      }

      const updatedVisible = visible !== undefined ? visible : state.traceInfo?.visible || false

      return {
        ...state,
        traceInfo: {
          visible: updatedVisible,
          traces: currentTraces,
          loadedThreadIds: currentLoadedThreadIds,
        },
      }
    }
    case 'REMOVE_TRACE': {
      const currentTraces = new Map(state.traceInfo?.traces || [])

      // Find and remove trace by actualTraceId
      for (const [messageId, traceData] of currentTraces) {
        if (traceData.actualTraceId === action.traceID) {
          currentTraces.delete(messageId)
          break
        }
      }

      return {
        ...state,
        traceInfo: {
          visible: state.traceInfo?.visible || false,
          traces: currentTraces,
          loadedThreadIds: state.traceInfo?.loadedThreadIds || new Set(),
        },
      }
    }
    case 'REMOVE_TRACES_FOR_THREAD': {
      const currentTraces = new Map(state.traceInfo?.traces || [])

      // Delete all traces for thread
      for (const [msgId, traceData] of currentTraces) {
        if (traceData.threadId === action.threadID) {
          currentTraces.delete(msgId)
        }
      }

      return {
        ...state,
        traceInfo: {
          visible: state.traceInfo?.visible || false,
          traces: currentTraces,
          loadedThreadIds: state.traceInfo?.loadedThreadIds || new Set(),
        },
      }
    }
  }
}
